/**
 * Hook pour gérer les couleurs de templates d'une société
 */

import { useState, useEffect } from 'react';
import { templateColorsAPI } from '../utils/api';
import { logger } from '../utils/logger';
import { prismaToUI, type LogoPositionUI } from '../utils/logoPosition';

interface TemplatePreferences {
  id: string;
  companyId: string;
  colorPrimary?: string;
  colorSecondary?: string;
  useCompanyColors?: boolean;
  customCss?: string;
  invoiceTemplateId?: string;
  quoteTemplateId?: string;
  logoPosition?: LogoPositionUI; // Format UI (kebab-case)
  updatedAt: Date;
}

interface UseTemplateColorsResult {
  preferences: TemplatePreferences | null;
  loading: boolean;
  error: string | null;
  updateColors: (colors: {
    colorPrimary?: string;
    colorSecondary?: string;
    useCompanyColors?: boolean;
  }) => Promise<void>;
  reload: () => Promise<void>;
}

/**
 * Hook pour charger et mettre à jour les couleurs de template
 *
 * @param companyId - ID de la société
 * @returns Préférences, loading state, error, et fonctions de mise à jour
 *
 * @example
 * ```tsx
 * const { preferences, loading, updateColors } = useTemplateColors(companyId);
 *
 * const handleColorChange = async () => {
 *   await updateColors({
 *     colorPrimary: '#0D9488',
 *     colorSecondary: '#10b981'
 *   });
 * };
 * ```
 */
export function useTemplateColors(companyId: string | undefined): UseTemplateColorsResult {
  const [preferences, setPreferences] = useState<TemplatePreferences | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Charger les préférences de template
   */
  const loadPreferences = async () => {
    if (!companyId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await templateColorsAPI.getPreferences(companyId);

      if (response.success && response.data) {
        // Prendre le premier élément si c'est un tableau
        const prefs = Array.isArray(response.data) ? response.data[0] : response.data;

        // Convertir logoPosition du format Prisma (TOP_LEFT) vers UI (top-left)
        const normalizedPrefs = {
          ...prefs,
          logoPosition: prefs.logoPosition ? prismaToUI(prefs.logoPosition as any) : 'top-left',
        };

        setPreferences(normalizedPrefs);
        logger.log('✅ Préférences de template chargées:', normalizedPrefs);
      } else {
        // Aucune préférence n'existe encore, initialiser avec valeurs par défaut
        setPreferences({
          id: '',
          companyId,
          colorPrimary: '#0D9488',
          colorSecondary: '#10b981',
          logoPosition: 'top-left',
          updatedAt: new Date(),
        });
        logger.log('ℹ️ Aucune préférence existante, utilisation des valeurs par défaut');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Erreur lors du chargement des préférences';
      setError(errorMessage);
      logger.error('❌ Erreur chargement préférences:', err);

      // En cas d'erreur, initialiser avec valeurs par défaut
      setPreferences({
        id: '',
        companyId: companyId!,
        colorPrimary: '#0D9488',
        colorSecondary: '#10b981',
        logoPosition: 'top-left',
        updatedAt: new Date(),
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Mettre à jour les couleurs de template
   */
  const updateColors = async (colors: {
    colorPrimary?: string;
    colorSecondary?: string;
    useCompanyColors?: boolean;
  }) => {
    if (!companyId) {
      throw new Error('Company ID requis');
    }

    setLoading(true);
    setError(null);

    try {
      logger.log('🎨 Mise à jour couleurs:', colors);

      const response = await templateColorsAPI.updateColors(companyId, colors);

      if (response.success && response.data) {
        setPreferences(response.data);
        logger.log('✅ Couleurs mises à jour avec succès');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Erreur lors de la mise à jour des couleurs';
      setError(errorMessage);
      logger.error('❌ Erreur mise à jour couleurs:', err);
      throw err; // Propager l'erreur pour l'UI
    } finally {
      setLoading(false);
    }
  };

  /**
   * Recharger les préférences
   */
  const reload = async () => {
    await loadPreferences();
  };

  // Charger au montage et quand companyId change
  useEffect(() => {
    loadPreferences();
  }, [companyId]);

  return {
    preferences,
    loading,
    error,
    updateColors,
    reload,
  };
}
